import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import Table, { Column } from 'ui/Table';
import StatCard from 'ui/StatCard';
import Loading from 'ui/Loading';
import Modal from 'ui/Modal';
import Input from 'ui/Input';
import Button from 'ui/Button';
import ImageUploader, { ImageFile } from 'ui/ImageUploader';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import StatsGrid from 'ui/StatsGrid';
import FilterSection from 'ui/FilterSection';
import TableContainer from 'ui/TableContainer';
import SearchInput from 'ui/SearchInput';
import { Badge } from 'ui/shadcn';
import { patentApi, Patent, CreatePatentDto, UpdatePatentDto } from 'apis/patent';
import { toast } from 'react-toastify';

interface PatentForm {
    patent_number: string;
    application_number: string;
    application_date: string;
    registration_date: string;
    expiry_date: string;
    invention_title: string;
    patent_holder: string;
    inventors: string;
    certificate_images: ImageFile[];
}

const PatentManagementPage: React.FC = () => {
    const [patents, setPatents] = useState<Patent[]>([]);
    const [filteredPatents, setFilteredPatents] = useState<Patent[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingPatent, setEditingPatent] = useState<Patent | null>(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);

    const [patentForm, setPatentForm] = useState<PatentForm>({
        patent_number: '',
        application_number: '',
        application_date: '',
        registration_date: '',
        expiry_date: '',
        invention_title: '',
        patent_holder: '',
        inventors: '',
        certificate_images: []
    });

    const [searchParams, setSearchParams] = useSearchParams();

    // Fetch patents from API
    useEffect(() => {
        fetchPatents();
    }, []);

    // ?edit=<id> 쿼리 파람으로 진입 시 해당 특허 수정 모달 자동 열기
    useEffect(() => {
        const editId = searchParams.get('edit');
        if (!editId || patents.length === 0) return;
        const target = patents.find(p => p.id === Number(editId));
        if (target) {
            handleEdit(target);
            // 쿼리 파람 정리 (모달 닫고 다시 들어와도 또 안 열리게)
            searchParams.delete('edit');
            setSearchParams(searchParams, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patents, searchParams]);

    const fetchPatents = async () => {
        try {
            setLoading(true);
            const response = await patentApi.getPatents({ limit: 1000 });
            setPatents(response.data.data);
            setFilteredPatents(response.data.data);
        } catch (error) {
            console.error('[Patent] Failed to fetch patents:', error);
            toast.error('특허 정보를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    // 검색 및 필터링
    useEffect(() => {
        let filtered = patents;

        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(patent =>
                patent.invention_title.toLowerCase().includes(lowerSearch) ||
                patent.patent_number.toLowerCase().includes(lowerSearch) ||
                patent.application_number.toLowerCase().includes(lowerSearch) ||
                patent.patent_holder.toLowerCase().includes(lowerSearch) ||
                patent.inventors.toLowerCase().includes(lowerSearch)
            );
        }

        // Sort by registration date (newest first)
        filtered.sort((a, b) => {
            if (!a.registration_date) return 1;
            if (!b.registration_date) return -1;
            return new Date(b.registration_date).getTime() - new Date(a.registration_date).getTime();
        });

        setFilteredPatents(filtered);
    }, [patents, searchTerm]);

    const handleCreate = () => {
        setEditingPatent(null);
        setPatentForm({
            patent_number: '',
            application_number: '',
            application_date: '',
            registration_date: '',
            expiry_date: '',
            invention_title: '',
            patent_holder: '유원이엔에프 주식회사',
            inventors: '',
            certificate_images: []
        });
        setShowCreateModal(true);
    };

    const handleEdit = (patent: Patent) => {
        setEditingPatent(patent);
        setPatentForm({
            patent_number: patent.patent_number,
            application_number: patent.application_number,
            application_date: patent.application_date ? patent.application_date.split('T')[0] : '',
            registration_date: patent.registration_date ? patent.registration_date.split('T')[0] : '',
            expiry_date: patent.expiry_date ? patent.expiry_date.split('T')[0] : '',
            invention_title: patent.invention_title,
            patent_holder: patent.patent_holder,
            inventors: patent.inventors,
            certificate_images: patent.certificate_url ? [{
                id: '1',
                url: patent.certificate_url,
                name: 'certificate.jpg'
            }] : []
        });
        setShowCreateModal(true);
    };

    const handleImagesChange = (images: ImageFile[]) => {
        setPatentForm({ ...patentForm, certificate_images: images });
    };

    const handleSave = async () => {
        try {
            setUploading(true);

            let certificateUrl = '';

            // Upload certificate image if provided
            if (patentForm.certificate_images.length > 0) {
                const image = patentForm.certificate_images[0];
                if (image.file) {
                    try {
                        certificateUrl = await patentApi.uploadCertificate(image.file);
                    } catch (uploadError) {
                        console.error('[Patent] Failed to upload certificate:', uploadError);
                        toast.error('특허증 업로드에 실패했습니다.');
                        setUploading(false);
                        return;
                    }
                } else if (image.url) {
                    certificateUrl = image.url;
                }
            }

            const patentData = {
                patent_number: patentForm.patent_number,
                application_number: patentForm.application_number,
                application_date: patentForm.application_date,
                registration_date: patentForm.registration_date,
                expiry_date: patentForm.expiry_date,
                invention_title: patentForm.invention_title,
                patent_holder: patentForm.patent_holder,
                inventors: patentForm.inventors,
                certificate_url: certificateUrl
            };

            if (editingPatent) {
                // Update existing patent
                await patentApi.updatePatent(editingPatent.id, patentData as UpdatePatentDto);
                toast.success('특허 정보가 수정되었습니다.');
            } else {
                // Create new patent
                await patentApi.createPatent(patentData as CreatePatentDto);
                toast.success('특허가 등록되었습니다.');
            }

            setShowCreateModal(false);
            fetchPatents(); // Refresh the list
        } catch (error) {
            console.error('[Patent] Failed to save patent:', error);
            toast.error('특허 저장에 실패했습니다.');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (patentId: number) => {
        if (window.confirm('이 특허 정보를 삭제하시겠습니까?')) {
            try {
                await patentApi.deletePatent(patentId);
                toast.success('특허가 삭제되었습니다.');
                fetchPatents(); // Refresh the list
            } catch (error) {
                console.error('[Patent] Failed to delete patent:', error);
                toast.error('특허 삭제에 실패했습니다.');
            }
        }
    };

    const getStatistics = () => {
        const total = patents.length;
        const currentYear = new Date().getFullYear();
        const thisYear = patents.filter(p =>
            p.registration_date && new Date(p.registration_date).getFullYear() === currentYear
        ).length;
        const expiringSoon = patents.filter(p => {
            if (!p.expiry_date) return false;
            const expiryDate = new Date(p.expiry_date);
            const today = new Date();
            const diffTime = expiryDate.getTime() - today.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays <= 365 && diffDays > 0; // Within 1 year
        }).length;

        return { total, thisYear, expiringSoon };
    };

    const stats = getStatistics();

    const formatDate = (dateString?: string) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const renderDate = (value?: string) => (
        <span className="whitespace-nowrap tabular-nums">{formatDate(value)}</span>
    );

    const columns: Column<Patent>[] = [
        {
            key: 'id',
            header: '번호',
            width: '64px',
            align: 'center',
            sortable: true,
            render: (value) => <span className="tabular-nums text-muted-foreground">{value}</span>
        },
        {
            key: 'patent_number',
            header: '특허번호',
            width: '124px',
            align: 'center',
            sortable: true,
            render: (value) => <span className="whitespace-nowrap tabular-nums">{value}</span>
        },
        {
            key: 'invention_title',
            header: '발명의 명칭',
            width: '200px',
            sortable: true,
            render: (value) => <span className="break-keep">{value}</span>
        },
        {
            key: 'patent_holder',
            header: '특허권자',
            width: '140px',
            render: (value) => <span className="break-keep">{value}</span>
        },
        {
            key: 'inventors',
            header: '발명자',
            width: '90px',
            render: (value) => {
                const inventors = value.split(',');
                if (inventors.length > 2) {
                    return `${inventors[0]} 외 ${inventors.length - 1}명`;
                }
                return value;
            }
        },
        {
            key: 'application_date',
            header: '출원일',
            width: '108px',
            align: 'center',
            sortable: true,
            render: (value) => renderDate(value)
        },
        {
            key: 'registration_date',
            header: '등록일',
            width: '108px',
            align: 'center',
            sortable: true,
            render: (value) => renderDate(value)
        },
        {
            key: 'expiry_date',
            header: '만기일',
            width: '108px',
            align: 'center',
            sortable: true,
            render: (value) => {
                const formatted = formatDate(value);
                if (value) {
                    const expiryDate = new Date(value);
                    const today = new Date();
                    const diffTime = expiryDate.getTime() - today.getTime();
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                    if (diffDays < 0) {
                        return (
                            <div className="flex flex-col items-center gap-0.5">
                                <span className="whitespace-nowrap tabular-nums text-destructive">{formatted}</span>
                                <Badge variant="danger">만료</Badge>
                            </div>
                        );
                    } else if (diffDays <= 365) {
                        return <span className="whitespace-nowrap tabular-nums text-warning">{formatted}</span>;
                    }
                }
                return renderDate(value);
            }
        },
        {
            key: 'certificate_url',
            header: '특허증',
            width: '72px',
            align: 'center',
            render: (value) => value ? (
                <img
                    src={value}
                    alt="특허증"
                    className="mx-auto h-10 w-10 cursor-pointer rounded border border-border object-cover"
                    onClick={(e) => {
                        e.stopPropagation();
                        window.open(value, '_blank');
                    }}
                />
            ) : '-'
        }
    ];

    if (loading) {
        return (
            <PageContainer>
                <Loading text="특허 정보를 불러오는 중..." />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title="특허 관리"
                subtitle="보유 특허 현황 및 관리"
                actions={
                    <Button variant="primary" icon={<Plus />} onClick={handleCreate}>
                        특허 등록
                    </Button>
                }
            />

            <StatsGrid columns={3} className="mb-4">
                <StatCard
                    title="총 특허수"
                    value={stats.total}
                    suffix="건"
                />

                <StatCard
                    title="올해 등록"
                    value={stats.thisYear}
                    suffix="건"
                    valueColor="success"
                />

                <StatCard
                    title="만기 임박 (1년 이내)"
                    value={stats.expiringSoon}
                    suffix="건"
                    valueColor="warning"
                />
            </StatsGrid>

            <FilterSection>
                <SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="특허번호, 발명의 명칭, 특허권자, 발명자로 검색..."
                />
            </FilterSection>

            <TableContainer
                title="특허 목록"
                resultCount={filteredPatents.length}
            >
                <Table
                    columns={columns}
                    data={filteredPatents}
                    pagination
                    pageSize={10}
                    defaultSortKey="registration_date"
                    defaultSortOrder="desc"
                    stickyHeader
                    maxHeight="600px"
                    onRowClick={(patent) => handleEdit(patent)}
                    actionsColumnWidth="76px"
                    actions={(patent) => (
                        <Button
                            size="small"
                            variant="danger-outline"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(patent.id);
                            }}
                        >
                            삭제
                        </Button>
                    )}
                />
            </TableContainer>

            {/* 특허 등록/수정 모달 */}
            <Modal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title={editingPatent ? '특허 수정' : '특허 등록'}
                size="large"
                footer={
                    <>
                        <Button
                            variant="outline"
                            onClick={() => setShowCreateModal(false)}
                            disabled={uploading}
                        >
                            취소
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSave}
                            loading={uploading}
                            disabled={
                                !patentForm.patent_number ||
                                !patentForm.application_number ||
                                !patentForm.invention_title ||
                                !patentForm.patent_holder ||
                                !patentForm.inventors ||
                                !patentForm.application_date ||
                                !patentForm.registration_date ||
                                !patentForm.expiry_date
                            }
                        >
                            {editingPatent ? '수정' : '등록'}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Input
                            label="특허번호"
                            required
                            type="text"
                            value={patentForm.patent_number}
                            onChange={(e) => setPatentForm({...patentForm, patent_number: e.target.value})}
                            placeholder="예: 제10-1234567호"
                        />
                        <Input
                            label="출원번호"
                            required
                            type="text"
                            value={patentForm.application_number}
                            onChange={(e) => setPatentForm({...patentForm, application_number: e.target.value})}
                            placeholder="예: 제10-2023-0012345호"
                        />
                    </div>
                    <Input
                        label="발명의 명칭"
                        required
                        type="text"
                        value={patentForm.invention_title}
                        onChange={(e) => setPatentForm({...patentForm, invention_title: e.target.value})}
                        placeholder="발명의 명칭을 입력하세요"
                    />
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Input
                            label="특허권자"
                            required
                            type="text"
                            value={patentForm.patent_holder}
                            onChange={(e) => setPatentForm({...patentForm, patent_holder: e.target.value})}
                            placeholder="예: 유원이엔에프 주식회사"
                        />
                        <Input
                            label="발명자"
                            required
                            type="text"
                            value={patentForm.inventors}
                            onChange={(e) => setPatentForm({...patentForm, inventors: e.target.value})}
                            placeholder="콤마로 구분 (예: 홍길동, 김철수)"
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <Input
                            label="출원일"
                            required
                            type="date"
                            value={patentForm.application_date}
                            onChange={(e) => setPatentForm({...patentForm, application_date: e.target.value})}
                        />
                        <Input
                            label="등록일"
                            required
                            type="date"
                            value={patentForm.registration_date}
                            onChange={(e) => setPatentForm({...patentForm, registration_date: e.target.value})}
                        />
                        <Input
                            label="만기일"
                            required
                            type="date"
                            value={patentForm.expiry_date}
                            onChange={(e) => setPatentForm({...patentForm, expiry_date: e.target.value})}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium text-neutral-700">특허증 이미지</span>
                        <ImageUploader
                            images={patentForm.certificate_images}
                            onChange={handleImagesChange}
                            maxImages={1}
                            multiple={false}
                            accept="image/*"
                        />
                        <p className="text-xs text-muted-foreground">
                            JPG, PNG 등 이미지 파일 (최대 10MB)
                        </p>
                    </div>
                </div>
            </Modal>
        </PageContainer>
    );
};

export default PatentManagementPage;
